/* eslint-disable react/prop-types */
import { useQuery } from "@apollo/client";
import { Table } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import {
  useGroupTypes,
  useWorkspace,
} from "src/contexts/global/WorkspaceContext";
import {
  ALL_DEMAND_GROUP_TYPES,
  ALL_DOES_NOT_PARTICIPATE_GROUP_TYPES,
  ALL_SUPPLY_GROUP_TYPES,
  DEFAULT_ALLOWED_GROUP_TYPE,
} from "src/pages/Workspaces/consts";
import { listGroupsForTeamTable } from "src/queries/group.graphql";
import {
  getActiveDemandGroupTypes,
  getActiveGroupTypes,
  getActiveSupplyGroupTypes,
} from "src/util/group";

import { useAIDashboardStore } from "../context/AIDashboardContext";

const CanvTeamTable = ({
  channel,
  "show-data": showData,
  "team-types-channel": groupTypesChannel,
}) => {
  const store = useAIDashboardStore(channel);
  const publish = store((state) => state.publish);
  const workspace = useWorkspace();
  const groupTypes = useGroupTypes();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [columnFilters, setColumnFilters] = useState([]);

  // Subscribe to group types channel if provided
  // Use a dummy channel if none provided to avoid conditional hook calls
  const channelToUse = groupTypesChannel || "__no-channel__";
  const groupTypesStore = useAIDashboardStore(channelToUse);
  const storeValue = groupTypesStore((state) => state.value);
  const selectedGroupTypeKeys = groupTypesChannel ? storeValue : null;

  // Extract name filter value
  const nameFilter = columnFilters.find((f) => f.id === "name")?.value || "";

  // Convert selected group type keys to IDs for query
  const groupTypeIds = useMemo(() => {
    if (!selectedGroupTypeKeys || selectedGroupTypeKeys.length === 0) {
      return undefined;
    }

    const typeIds = [];

    selectedGroupTypeKeys.forEach((key) => {
      if (key === DEFAULT_ALLOWED_GROUP_TYPE.key) {
        // ALL - include all active group types
        const activeTypes = getActiveGroupTypes(groupTypes);
        activeTypes.forEach((type) => {
          typeIds.push(type.id);
        });
      } else if (key === ALL_DEMAND_GROUP_TYPES.key) {
        // ALL_DEMAND - include all demand types
        const demandTypes = getActiveDemandGroupTypes(groupTypes);
        demandTypes.forEach((type) => {
          typeIds.push(type.id);
        });
      } else if (key === ALL_SUPPLY_GROUP_TYPES.key) {
        // ALL_SUPPLY - include all supply types
        const supplyTypes = getActiveSupplyGroupTypes(groupTypes);
        supplyTypes.forEach((type) => {
          typeIds.push(type.id);
        });
      } else if (key === ALL_DOES_NOT_PARTICIPATE_GROUP_TYPES.key) {
        // ALL_DOES_NOT_PARTICIPATE - include all does not participate types
        const activeTypes = getActiveGroupTypes(groupTypes);
        activeTypes.forEach((type) => {
          if (!type.isDemand && !type.isSupply) {
            typeIds.push(type.id);
          }
        });
      } else {
        // Individual group type
        typeIds.push(key);
      }
    });

    // Remove duplicates
    return [...new Set(typeIds)];
  }, [selectedGroupTypeKeys, groupTypes]);

  const { data, loading, error } = useQuery(listGroupsForTeamTable, {
    variables: {
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      includeHiddenGroups: false,
      includeDeletedGroups: false,
      search: nameFilter ? [nameFilter] : undefined,
      groupTypes: groupTypeIds,
    },
  });

  useEffect(() => {
    if (data?.result) {
      publish(data?.result?.groups || []);
    }
  }, [data, publish]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 150,
        enableColumnFilter: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 200,
        Cell: ({ row }) => (
          <a
            href={`/${workspace?.id}/teams/${row.original.id}/details`}
            style={{ color: "#1976d2", textDecoration: "none" }}
          >
            {row.original.name}
          </a>
        ),
      },
      {
        accessorKey: "memberCount",
        header: "Size",
        size: 80,
        enableColumnFilter: false,
      },
      {
        accessorKey: "childTeamCount",
        header: "Child Team Count",
        size: 140,
        enableColumnFilter: false,
      },
      {
        accessorKey: "hierarchyIds",
        header: "Hierarchy Level",
        size: 130,
        accessorFn: (row) => row.hierarchyIds?.length || 0,
        enableColumnFilter: false,
      },
      {
        accessorKey: "objectiveIds",
        header: "Objectives",
        size: 100,
        accessorFn: (row) => row.objectiveIds?.length || 0,
        enableColumnFilter: false,
      },
    ],
    [workspace?.id]
  );

  const teams = data?.result?.groups || [];
  const hasMore = data?.result?.hasMore || false;

  // Calculate rowCount for pagination
  // If we have more pages, we know there are at least (pageIndex + 2) * pageSize rows
  const rowCount = hasMore
    ? (pagination.pageIndex + 2) * pagination.pageSize
    : pagination.pageIndex * pagination.pageSize + teams.length;

  return (
    <Table
      key={
        groupTypeIds ? JSON.stringify(groupTypeIds.slice().sort()) : "no-filter"
      }
      columns={columns}
      data={showData === "true" ? teams : []}
      renderEmptyRowsFallback={() => ""}
      enableTopToolbar
      enableBottomToolbar
      enableColumnFilters
      enableGlobalFilter={false}
      enableSorting={false}
      manualFiltering
      manualPagination
      rowCount={rowCount}
      onColumnFiltersChange={setColumnFilters}
      onPaginationChange={setPagination}
      state={{
        columnFilters,
        columnVisibility: { id: false },
        pagination,
        isLoading: loading,
        showAlertBanner: !!error,
        showProgressBars: loading,
        showColumnFilters: true,
      }}
      muiToolbarAlertBannerProps={
        error
          ? {
              color: "error",
              children: `Error loading teams: ${error.message}`,
            }
          : undefined
      }
      initialState={{
        density: "compact",
        showColumnFilters: true,
        columnVisibility: { id: false },
      }}
    />
  );
};

CanvTeamTable.propTypes = {
  channel: PropTypes.string.isRequired,
  "show-data": PropTypes.string.isRequired,
  "team-types-channel": PropTypes.string,
};

export default CanvTeamTable;
